import { useFormik } from "formik"

import { getProvinceArray } from "src/components/Account/BillingPortal/countryData"
import { RecursivePartial } from "src/data/commonTypes"
import { useFetchShippingDestinations } from "src/data/ecommerce/ecommerceQueries"
import { TParadiseCreateUserBody } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { validatePhoneNumber } from "src/ui/PhoneNumberInput/phoneNumberUtil"

export type TParadiseCreateUserForm = {
  fullname: string
  email: string
  preferredLanguage: string
  subscribeNewsletter: boolean
  adminRole: TParadiseCreateUserBody["admin_role"] | undefined
  billingAddress?: {
    firstName: string
    lastName: string
    phone: string
    email: string
    line1: string
    city: string
    stateCode?: string
    zip: string
    country: string
  }
}

const initialValues: TParadiseCreateUserForm = {
  fullname: "",
  email: "",
  preferredLanguage: "en",
  subscribeNewsletter: false,
  adminRole: undefined,
  billingAddress: undefined,
}

const initialBillingAddress: TParadiseCreateUserForm["billingAddress"] = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  line1: "",
  city: "",
  stateCode: undefined,
  zip: "",
  country: "",
}

export function useParadiseCreateUser({
  onSubmit,
}: {
  onSubmit: (values: TParadiseCreateUserForm) => void
}) {
  const form = useFormik<TParadiseCreateUserForm>({
    initialValues,
    onSubmit,
    validate: (values) => {
      const errors: RecursivePartial<TParadiseCreateUserForm> = {}

      if (
        values.billingAddress &&
        (!values.billingAddress?.phone ||
          !validatePhoneNumber(values.billingAddress.phone))
      ) {
        if (!errors.billingAddress) {
          errors.billingAddress = {}
        }

        errors.billingAddress.phone = "Invalid phone number"
      }

      return errors
    },
    validateOnChange: false,
  })

  const fetchAvailableCountries = useFetchShippingDestinations()

  const countryStates = getProvinceArray(form.values.billingAddress?.country)

  function initializeBillingAddress() {
    form.setValues({
      ...form.values,
      billingAddress: initialBillingAddress,
    })
  }

  function clearBillingAddress() {
    form.setValues({
      ...form.values,
      billingAddress: undefined,
    })
  }

  function updateBillingAddress(
    billingAddress: Partial<TParadiseCreateUserForm["billingAddress"]>
  ) {
    if (!form.values.billingAddress) {
      return
    }

    form.setValues({
      ...form.values,
      billingAddress: { ...form.values.billingAddress, ...billingAddress },
    })
  }

  return {
    form: {
      ...form,
      setFieldValue: <T extends keyof TParadiseCreateUserForm>(
        field: T,
        value: TParadiseCreateUserForm[T]
      ) => {
        form.setFieldValue(field, value)
      },
    },
    updateBillingAddress,
    initializeBillingAddress,
    clearBillingAddress,
    availableCountryCodes: fetchAvailableCountries.data?.map(
      (destination) => destination.country_code
    ),
    availableStates: countryStates,
    isLoadingAvailableCountries: fetchAvailableCountries.isLoading,
  }
}
